import { useEffect, useState, createElement } from "react";
import { toast } from "sonner";
import API, { fmt } from "@/lib/api";
import { PageHeader, PackageBadge, StatusPill, Loader } from "@/components/ui-bits";
import { ChevronRight, ChevronDown, User, Phone, Copy } from "lucide-react";

function TreeNode({ node, level }) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!node.has_children) return;
    if (!open && children === null) {
      setLoading(true);
      try {
        const { data } = await API.get("/team", { params: { parent_id: node.id } });
        setChildren(data.children);
      } finally { setLoading(false); }
    }
    setOpen(!open);
  };

  const copyMobile = (e) => {
    e.stopPropagation();
    if (!node.mobile) return;
    navigator.clipboard.writeText(node.mobile);
    toast.success("Mobile number copied");
  };

  return (
    <div className="ml-0">
      <div
        data-testid={`tree-node-${node.id}`}
        onClick={toggle}
        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border border-white/5 bg-white/[0.03] mb-1.5 ${node.has_children ? "cursor-pointer hover:border-emerald-500/30" : ""}`}
      >
        <span className="w-4 text-slate-500 shrink-0">
          {node.has_children ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}
        </span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 text-emerald-400 border border-emerald-500/20">
          <User className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-medium break-words" data-testid={`node-name-${node.id}`}>
            {node.name} <span className="text-xs text-slate-500 font-num">· {node.referral_code}</span>
          </div>
          {node.mobile ? (
            <button onClick={copyMobile} data-testid={`node-mobile-${node.id}`}
              className="mt-0.5 inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 font-num">
              <Phone className="h-3 w-3" /> {node.mobile} <Copy className="h-3 w-3 opacity-70" />
            </button>
          ) : null}
          <div className="text-xs text-slate-500">Joined {new Date(node.join_date).toLocaleDateString()} · {node.children_count} direct</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <PackageBadge id={node.package_id} />
          <StatusPill status={node.status} />
        </div>
      </div>
      {open && (
        <div className="ml-6 border-l border-white/10 pl-3">
          {loading && <div className="text-xs text-slate-500 py-2">Loading...</div>}
          {children && children.length === 0 && <div className="text-xs text-slate-500 py-2">No downline.</div>}
          {children && children.map((c) => createElement(TreeNode, { key: c.id, node: c, level: level + 1 }))}
        </div>
      )}
    </div>
  );
}

export default function Team() {
  const [root, setRoot] = useState(null);

  useEffect(() => {
    API.get("/team").then((r) => setRoot(r.data.children));
  }, []);

  if (!root) return <Loader label="Loading team" />;

  return (
    <div>
      <PageHeader title="Team / Downline Tree" subtitle="Explore your 6-level downline. Click a member to lazy-load their team." testid="team-header" />
      <div className="glass rounded-2xl p-5" data-testid="team-tree">
        {root.length === 0 ? (
          <p className="text-sm text-slate-500 py-10 text-center">No downline yet. Invite members with your referral link.</p>
        ) : (
          root.map((n) => <TreeNode key={n.id} node={n} level={1} />)
        )}
      </div>
    </div>
  );
}
